import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Mail, CheckCircle, XCircle, Clock, ExternalLink, Eye, Edit2, Trash2, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ProofApprovalDialog } from "./ProofApprovalDialog";
import { BatchProofApprovalDialog } from "./BatchProofApprovalDialog";

const API = process.env.REACT_APP_BACKEND_URL || "";

export function ProofApprovalManagement({ events }) {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingApproval, setEditingApproval] = useState(null);
  const [filterStatus, setFilterStatus] = useState("pending");
  const [filterEvent, setFilterEvent] = useState("all");

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/proof/proof-approvals`);
      setApprovals(response.data);
    } catch (error) {
      toast.error("Failed to load proof approvals");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleCreateNew = () => {
    if (!selectedEvent) {
      toast.error("Please select an event first");
      return;
    }
    setDialogOpen(true);
  };

  const handleEdit = (approval) => {
    setEditingApproval(approval);
    setEditDialogOpen(true);
  };

  const handleResend = async (approvalId) => {
    if (!window.confirm("Resend proof approval email to customer?")) return;
    
    try {
      await axios.post(`${API}/api/proof/proof-approvals/${approvalId}/resend`);
      toast.success("Email resent successfully!");
    } catch (error) {
      toast.error("Failed to resend email");
    }
  };

  const handleDelete = async (approvalId, artworkName, status) => {
    if (!window.confirm(`Delete approval for "${artworkName}" (${status})? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/api/proof/proof-approvals/${approvalId}`);
      toast.success("Proof approval deleted successfully");
      fetchApprovals();
    } catch (error) {
      toast.error("Failed to delete approval");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge className="bg-yellow-500 text-white"><Clock className="h-3 w-3 mr-1" />Pending</Badge>,
      approved: <Badge className="bg-green-500 text-white"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>,
      rejected: <Badge className="bg-red-500 text-white"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>
    };
    return badges[status] || badges.pending;
  };

  const filteredApprovals = approvals.filter(approval => {
    const statusMatch = filterStatus === "all" || approval.status === filterStatus;
    const eventMatch = filterEvent === "all" || approval.event_id === filterEvent;
    return statusMatch && eventMatch;
  });

  const selectedEventData = events.find(e => e.id === selectedEvent);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-semibold tracking-tight">Proof Approval Management</h2>
        <div className="flex gap-3">
          <Select value={selectedEvent || ""} onValueChange={setSelectedEvent}>
            <SelectTrigger className="w-[200px] rounded-none border-2">
              <SelectValue placeholder="Select Event" />
            </SelectTrigger>
            <SelectContent>
              {events.map(event => (
                <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={async () => {
              try {
                const response = await axios.post(`${API}/api/proof/proof-approvals/send-reminders`);
                toast.success(`Sent ${response.data.reminders_sent} reminder emails`);
              } catch (error) {
                toast.error("Failed to send reminders");
              }
            }}
            variant="outline"
            className="rounded-none border-2"
            title="Send 24-hour reminder emails to customers with pending approvals"
          >
            <Clock className="h-4 w-4 mr-2" />
            Send Reminders
          </Button>
          <Button
            data-testid="cleanup-duplicates-button"
            onClick={async () => {
              try {
                const dry = await axios.post(`${API}/api/proof/proof-approvals/cleanup-superseded?dry_run=true`);
                const found = dry.data.found || 0;
                if (found === 0) {
                  toast.info("No stale duplicates found");
                  return;
                }
                const lines = dry.data.details.slice(0, 10).map(d => `• ${d.artwork_name} (${d.customer_email})`).join("\n");
                const more = dry.data.details.length > 10 ? `\n…and ${dry.data.details.length - 10} more` : "";
                if (!window.confirm(`Found ${found} pending approval${found === 1 ? "" : "s"} superseded by an approved/rejected duplicate:\n\n${lines}${more}\n\nMark them as 'superseded' (stops reminders)?`)) return;
                const res = await axios.post(`${API}/api/proof/proof-approvals/cleanup-superseded?dry_run=false`);
                toast.success(`Cleaned up ${res.data.applied} stale duplicate${res.data.applied === 1 ? "" : "s"}`);
                await fetchApprovals();
              } catch (e) {
                toast.error("Cleanup failed: " + (e.response?.data?.detail || e.message));
              }
            }}
            variant="outline"
            className="rounded-none border-2"
            title="Find pending approvals that already have an approved/rejected duplicate, and stop their reminders"
          >
            <LifeBuoy className="h-4 w-4 mr-2" />
            Cleanup Duplicates
          </Button>
          <Button
            onClick={handleCreateNew}
            disabled={!selectedEvent}
            className="bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none"
            data-testid="create-proof-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Send New Proof for Approval
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium mb-2">Filter by Status:</label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px] rounded-none border-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Filter by Event:</label>
          <Select value={filterEvent} onValueChange={setFilterEvent}>
            <SelectTrigger className="w-[180px] rounded-none border-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {events.map(event => (
                <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Approvals Table */}
      {loading ? (
        <div className="flex items-center justify-center h-[400px]">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      ) : filteredApprovals.length === 0 ? (
        <div className="border border-border bg-white p-12 text-center">
          <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Proof Approvals Yet</h3>
          <p className="text-gray-600 mb-4">
            {filterStatus !== "all" || filterEvent !== "all"
              ? "No approvals match your filters"
              : "Select an event above and click 'Send New Proof for Approval' to get started"}
          </p>
        </div>
      ) : (
        <div className="border border-border bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Event Code</TableHead>
                <TableHead className="font-semibold">Artwork</TableHead>
                <TableHead className="font-semibold">Customer</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Created</TableHead>
                <TableHead className="font-semibold">Last Reminder</TableHead>
                <TableHead className="font-semibold">Submitted</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredApprovals.map((approval) => (
                <TableRow key={approval.id}>
                  <TableCell className="font-mono text-sm">{approval.event_code}</TableCell>
                  <TableCell className="font-medium">{approval.artwork_name}</TableCell>
                  <TableCell>{approval.customer_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{approval.customer_email}</TableCell>
                  <TableCell>{getStatusBadge(approval.status)}</TableCell>
                  <TableCell className="text-sm">
                    {new Date(approval.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-sm">
                    {approval.last_reminder_sent ? (
                      <span title={new Date(approval.last_reminder_sent).toLocaleString()}>
                        {new Date(approval.last_reminder_sent).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Never</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {approval.submitted_at ? new Date(approval.submitted_at).toLocaleDateString() : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(`/approve/${approval.id}`, '_blank')}
                        className="h-8 px-2 rounded-none"
                        title="View Approval Page"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(approval.google_drive_link, '_blank')}
                        className="h-8 px-2 rounded-none"
                        title="Open Google Drive Link"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      {(approval.status === 'pending' || approval.status === 'rejected') && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(approval)}
                            className="h-8 px-2 rounded-none text-blue-600 hover:text-blue-700"
                            title="Edit Proof Details"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResend(approval.id)}
                            className="h-8 px-2 rounded-none text-green-600 hover:text-green-700"
                            title="Resend Email"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(approval.id, approval.artwork_name, approval.status)}
                        className="h-8 px-2 rounded-none text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete Approval"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-4 mt-6">
        <div className="bg-white border border-border p-4">
          <p className="text-sm text-muted-foreground">Total Approvals</p>
          <p className="text-2xl font-bold">{approvals.length}</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 p-4">
          <p className="text-sm text-yellow-700">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">
            {approvals.filter(a => a.status === "pending").length}
          </p>
        </div>
        <div className="bg-green-50 border border-green-200 p-4">
          <p className="text-sm text-green-700">Approved</p>
          <p className="text-2xl font-bold text-green-600">
            {approvals.filter(a => a.status === "approved").length}
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-700">Rejected</p>
          <p className="text-2xl font-bold text-red-600">
            {approvals.filter(a => a.status === "rejected").length}
          </p>
        </div>
      </div>

      {/* Create Dialog - Use Batch */}
      {selectedEventData && (
        <BatchProofApprovalDialog
          eventId={selectedEventData.id}
          eventName={selectedEventData.name}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={() => {
            fetchApprovals();
            toast.success("Proof approval request sent!");
          }}
        />
      )}

      {/* Edit Dialog */}
      {editingApproval && (
        <EditProofDialog
          approval={editingApproval}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSuccess={() => {
            fetchApprovals();
            setEditingApproval(null);
            toast.success("Proof updated successfully!");
          }}
        />
      )}
    </div>
  );
}

// Edit Proof Dialog Component
function EditProofDialog({ approval, open, onOpenChange, onSuccess }) {
  const [formData, setFormData] = useState({
    artwork_name: approval.artwork_name,
    event_code: approval.event_code,
    google_drive_link: approval.google_drive_link,
    customer_name: approval.customer_name,
    customer_email: approval.customer_email,
    notes: approval.notes || ""
  });
  const [updating, setUpdating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setUpdating(true);
    try {
      const params = new URLSearchParams(formData).toString();
      await axios.put(`${API}/api/proof/proof-approvals/${approval.id}?${params}`);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error("Failed to update proof approval");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Edit Proof Approval</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium mb-1">Artwork Name *</label>
            <Input
              value={formData.artwork_name}
              onChange={(e) => setFormData({ ...formData, artwork_name: e.target.value })}
              className="rounded-none border-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Event Code *</label>
            <Input
              value={formData.event_code}
              onChange={(e) => setFormData({ ...formData, event_code: e.target.value })}
              className="rounded-none border-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Google Drive Proof Link * (Update with new proof)</label>
            <Input
              type="url"
              value={formData.google_drive_link}
              onChange={(e) => setFormData({ ...formData, google_drive_link: e.target.value })}
              placeholder="https://drive.google.com/..."
              className="rounded-none border-2"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Customer Name *</label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                className="rounded-none border-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Customer Email *</label>
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                className="rounded-none border-2"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="rounded-none border-2 min-h-[80px]"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={updating}
              className="flex-1 bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none"
            >
              {updating ? "Updating..." : "Update Proof"}
            </Button>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="rounded-none"
            >
              Cancel
            </Button>
          </div>

          <p className="text-sm text-gray-600 mt-4">
            💡 After updating, click the <Mail className="inline h-4 w-4" /> Resend button to email the updated proof to the customer.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

